function hasValue(value) {
  return String(value ?? "").trim() !== "";
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function isValidAbsoluteUrl(value) {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    const hostname = String(parsed.hostname ?? "").toLowerCase().trim();
    if (!hostname) return false;
    if (hostname === "localhost") return true;
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
      return hostname
        .split(".")
        .map(Number)
        .every((part) => Number.isInteger(part) && part >= 0 && part <= 255);
    }
    if (!hostname.includes(".")) return false;
    if (hostname.startsWith(".") || hostname.endsWith(".")) return false;
    if (hostname.includes("..")) return false;

    const labels = hostname.split(".");
    if (labels.some((label) => !label || !/^[a-z0-9-]+$/i.test(label) || label.startsWith("-") || label.endsWith("-"))) {
      return false;
    }
    const tld = labels[labels.length - 1];
    if (!/^[a-z]{2,}$/i.test(tld)) return false;
    return true;
  } catch {
    return false;
  }
}

function isValidUsPhone(value) {
  return /^(?:\+1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/.test(value);
}

function isValidIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  const [y, m, d] = value.split("-").map(Number);
  return date.getFullYear() === y && (date.getMonth() + 1) === m && date.getDate() === d;
}

export function normalizeDateToIso(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (isValidIsoDate(raw)) return raw;
  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!usMatch) return "";
  const month = usMatch[1].padStart(2, "0");
  const day = usMatch[2].padStart(2, "0");
  const year = usMatch[3];
  const iso = `${year}-${month}-${day}`;
  return isValidIsoDate(iso) ? iso : "";
}

export function formatIsoDateToUs(value) {
  const iso = normalizeDateToIso(value);
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  return `${month}/${day}/${year}`;
}

export function getAboutFieldFormatError(path, value) {
  const input = String(value ?? "").trim();
  if (!input) return "";

  if (path === "author.email" || path === "vendor.email") {
    return isValidEmail(input) ? "" : "Enter a valid email address (for example: name@example.com).";
  }
  if (path === "author.phone" || path === "vendor.phone") {
    return isValidUsPhone(input) ? "" : "Enter a valid US phone number (for example: 202-555-1212).";
  }
  if (
    path === "author.website"
    || path === "vendor.website"
    || path === "repository"
    || path === "feedback"
    || path.startsWith("related_acrs[")
  ) {
    return isValidAbsoluteUrl(input) ? "" : "Enter a valid URL (for example: https://www.example.com).";
  }
  if (path === "report_date") {
    return normalizeDateToIso(input) ? "" : "Enter a valid date.";
  }

  return "";
}

export function validateAboutReportFields(evaluation) {
  const failures = [];
  const pushFailure = (path, fieldId, label, message) => {
    failures.push({
      path,
      fieldId,
      label,
      message,
    });
  };

  const product = evaluation?.product ?? {};
  // Unknown products are intentionally user-entered names that aren't in the author's org. They are
  // download-only (autosave/submit disabled), so we don't validate product identity for them.
  const isUnknownProduct = Boolean(evaluation?.product_is_unknown);
  const hasProductId = product?.id != null && String(product.id).trim() !== "";
  const hasNewProductValues = hasValue(product?.name)
    && hasValue(product?.version)
    && hasValue(product?.description);
  if (!isUnknownProduct && !hasProductId && !hasNewProductValues) {
    pushFailure(
      "product",
      "evaluation-product-name",
      "Product",
      "Select an existing product or provide new product details."
    );
  }

  const emailFields = [
    { path: "author.email", fieldId: "evaluation-author-email", label: "Author email", value: evaluation?.author?.email },
    { path: "vendor.email", fieldId: "evaluation-vendor-email", label: "Vendor email", value: evaluation?.vendor?.email },
  ];
  emailFields.forEach((field) => {
    const message = getAboutFieldFormatError(field.path, field.value);
    if (message) {
      pushFailure(field.path, field.fieldId, field.label, message);
    }
  });

  const phoneFields = [
    { path: "author.phone", fieldId: "evaluation-author-phone", label: "Author phone", value: evaluation?.author?.phone },
    { path: "vendor.phone", fieldId: "evaluation-vendor-phone", label: "Vendor phone", value: evaluation?.vendor?.phone },
  ];
  phoneFields.forEach((field) => {
    const message = getAboutFieldFormatError(field.path, field.value);
    if (message) {
      pushFailure(field.path, field.fieldId, field.label, message);
    }
  });

  const urlFields = [
    { path: "author.website", fieldId: "evaluation-author-website", label: "Author website", value: evaluation?.author?.website },
    { path: "vendor.website", fieldId: "evaluation-vendor-website", label: "Vendor website", value: evaluation?.vendor?.website },
    { path: "repository", fieldId: "evaluation-repository", label: "Repository URL", value: evaluation?.repository },
    { path: "feedback", fieldId: "evaluation-feedback", label: "Feedback URL", value: evaluation?.feedback },
  ];
  urlFields.forEach((field) => {
    const message = getAboutFieldFormatError(field.path, field.value);
    if (message) {
      pushFailure(field.path, field.fieldId, field.label, message);
    }
  });

  const reportDateMessage = getAboutFieldFormatError("report_date", evaluation?.report_date);
  if (reportDateMessage) {
    pushFailure("report_date", "evaluation-report-date", "Report date", reportDateMessage);
  }

  const relatedAcrs = Array.isArray(evaluation?.related_acrs) ? evaluation.related_acrs : [];
  relatedAcrs.forEach((item, index) => {
    const path = `related_acrs[${index}].url`;
    const message = getAboutFieldFormatError(path, item?.url);
    if (message) {
      pushFailure(
        path,
        `related-acr-url-${index}`,
        `Related ACR URL ${index + 1}`,
        message
      );
    }
  });

  return {
    isValid: failures.length === 0,
    failures,
  };
}

